import { useState, useEffect, useRef, useContext } from 'react';
import { Search, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import BarChart from '../../components/UI/BarChart';
import { Link } from 'react-router-dom';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.css';
import { getRoleBasedRoute } from '../../utils/routeUtils';
import { AuthContext } from '../../contexts/AuthContext';

// Sample data for employee attendance
const initialEmployeeData = [
  { empId: '1273053', fullName: 'Akash Munda', date: '30-05-2024', status: 'Active', clockIn: '9:00 AM', clockOut: '5:00 PM', workHours: '8 hours' },
  { empId: '1273053', fullName: 'Akash Munda', date: '30-05-2024', status: 'Inactive', clockIn: '9:00 AM', clockOut: '5:00 PM', workHours: '8 hours' },
  { empId: '1273053', fullName: 'Akash Munda', date: '30-05-2024', status: 'Active', clockIn: '9:00 AM', clockOut: '5:00 PM', workHours: '8 hours' },
  { empId: '1273053', fullName: 'Akash Munda', date: '30-05-2024', status: 'Active', clockIn: '9:00 AM', clockOut: '5:00 PM', workHours: '8 hours' },
  { empId: '1273053', fullName: 'Akash Munda', date: '30-05-2024', status: 'Active', clockIn: '9:00 AM', clockOut: '5:00 PM', workHours: '8 hours' },
  { empId: '1273053', fullName: 'Akash Munda', date: '30-05-2024', status: 'Inactive', clockIn: '9:00 AM', clockOut: '5:00 PM', workHours: '8 hours' },
  { empId: '1273053', fullName: 'Akash Munda', date: '30-05-2024', status: 'Active', clockIn: '9:00 AM', clockOut: '5:00 PM', workHours: '8 hours' },
];

// Custom Flatpickr input component
function FlatpickrDateInput({ value, onChange, label }) {
  const inputRef = useRef();
  const wrapperRef = useRef();
  useEffect(() => {
    if (!inputRef.current) return;
    const fp = flatpickr(inputRef.current, {
      dateFormat: 'd-m-Y',
      defaultDate: value,
      onChange: (selectedDates, dateStr) => {
        onChange(dateStr);
      },
      allowInput: true,
      static: true,
      disableMobile: true,
      position: 'below right',
      positionElement: wrapperRef.current,
    });
    return () => fp.destroy();
  }, [onChange]);
  return (
    <div className="relative" ref={wrapperRef}>
      <div className="flex items-center gap-2 px-2 py-2 rounded border border-gray-300 bg-gray-100 dark:bg-gray-900 dark:text-white dark:border-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition text-left">
        <button
          type="button"
          ref={inputRef}
          className="flex items-center w-full mr-6"
          tabIndex={0}>
          <span className="text-[#7A7B7F] font-semibold mr-3 whitespace-nowrap">{label}</span>
          <span className="text-gray-700 dark:text-white flex-1 truncate">{value}</span>
        </button>
        <span className="pointer-events-none flex items-center">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13.373 1.88696H11.123V0.761963C11.123 0.662507 11.0835 0.567124 11.0132 0.496798C10.9429 0.426472 10.8475 0.386963 10.748 0.386963C10.6486 0.386963 10.5532 0.426472 10.4829 0.496798C10.4126 0.567124 10.373 0.662507 10.373 0.761963V1.88696H5.12305V0.761963C5.12305 0.662507 5.08354 0.567124 5.01321 0.496798C4.94289 0.426472 4.8475 0.386963 4.74805 0.386963C4.64859 0.386963 4.55321 0.426472 4.48288 0.496798C4.41256 0.567124 4.37305 0.662507 4.37305 0.761963V1.88696H2.12305C1.62595 1.88756 1.14938 2.08529 0.797879 2.4368C0.446378 2.7883 0.248642 3.26486 0.248047 3.76196V13.512C0.248642 14.0091 0.446378 14.4856 0.797879 14.8371C1.14938 15.1886 1.62595 15.3864 2.12305 15.387H13.373C13.8703 15.387 14.3472 15.1894 14.6989 14.8378C15.0505 14.4862 15.248 14.0092 15.248 13.512V3.76196C15.248 3.26468 15.0505 2.78777 14.6989 2.43614C14.3472 2.08451 13.8703 1.88696 13.373 1.88696ZM14.498 13.512C14.498 13.8103 14.3795 14.0965 14.1685 14.3075C13.9576 14.5184 13.6714 14.637 13.373 14.637H2.12305C1.82468 14.637 1.53853 14.5184 1.32755 14.3075C1.11657 14.0965 0.998047 13.8103 0.998047 13.512V7.13696H14.498V13.512ZM14.498 6.38696H0.998047V3.76196C0.998047 3.14096 1.50055 2.63696 2.12305 2.63696H4.37305V3.76196C4.37305 3.86142 4.41256 3.9568 4.48288 4.02713C4.55321 4.09745 4.64859 4.13696 4.74805 4.13696C4.8475 4.13696 4.94289 4.09745 5.01321 4.02713C5.08354 3.9568 5.12305 3.86142 5.12305 3.76196V2.63696H10.373V3.76196C10.373 3.86142 10.4126 3.9568 10.4829 4.02713C10.5532 4.09745 10.6486 4.13696 10.748 4.13696C10.8475 4.13696 10.9429 4.09745 11.0132 4.02713C11.0835 3.9568 11.123 3.86142 11.123 3.76196V2.63696H13.373C13.6714 2.63696 13.9576 2.75549 14.1685 2.96647C14.3795 3.17745 14.498 3.46359 14.498 3.76196V6.38696Z" fill="#7A7B7F"/>
          </svg>
        </span>
      </div>
    </div>
  );
}

export default function AttendanceDashboard() {
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('2024-05-30');
  const [endDate, setEndDate] = useState('2024-05-30');
  const [employeeData, setEmployeeData] = useState(initialEmployeeData);
  const [filteredData, setFilteredData] = useState(initialEmployeeData);
  const { roles } = useContext(AuthContext);

  const getRoleBasedRouteLocal = (route) => {
    return getRoleBasedRoute(route, roles);
  };

  // Filter data based on search term and dates
  useEffect(() => {
    // Helper to parse 'DD-MM-YYYY' to Date object
    const parseEmployeeDate = (str) => {
      const [day, month, year] = str.split('-').map(Number);
      return new Date(year, month - 1, day);
    };
    // Helper to parse 'YYYY-MM-DD' to Date object
    const parseInputDate = (str) => {
      const [year, month, day] = str.split('-').map(Number);
      return new Date(year, month - 1, day);
    };
    const start = parseInputDate(startDate);
    const end = parseInputDate(endDate);
    const filtered = employeeData.filter(employee => {
      const matchesSearch = (
        employee.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.empId.includes(searchTerm)
      );
      const empDate = parseEmployeeDate(employee.date);
      const inRange = empDate >= start && empDate <= end;
      return matchesSearch && inRange;
    });
    setFilteredData(filtered);
  }, [searchTerm, employeeData, startDate, endDate]);

  const handleUploadUsers = () => {
    alert('Upload users functionality would be implemented here.');
  };

  return (
    <div className="flex flex-col min-h-screen font-poppins">
      
      {/* Breadcrumb */}
      <nav className="bg-white dark:bg-gray-900 p-2 flex flex-wrap mb-6 rounded-xl md:my-2 my-1" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-2 rtl:space-x-reverse text-sm">
          <li>
              <div className="flex items-center">
              <Link
                  to={getRoleBasedRouteLocal("/admin/attendance_management")}
                  className="ms-1 text-sm sm:text-md font-medium text-gray-700 hover:text-blue-600 md:ms-2 dark:text-blue-400 dark:hover:text-blue-500"
              >
                  Attendance Management
              </Link>
              </div>
          </li>
          <li aria-current="page">
              <div className="flex items-center">
              <svg className="rtl:rotate-180 w-3 h-3 text-gray-400 mx-1" aria-hidden="true" fill="none" viewBox="0 0 6 10">
                  <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 9 4-4-4-4" />
              </svg>
              <span className="ms-1 font-medium text-gray-500 md:ms-2">Attendance Dashboard</span>
              </div>
          </li>
          </ol>
      </nav>

      {/* Header */}
      {/* <header className="py-2 flex justify-between items-center mx-2 px-2">
        <h1 className="text-2xl font-semibold text-gray-800 dark:text-white">Attendance Dashboard</h1>
        <button 
          onClick={handleUploadUsers}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
          Upload Users
        </button>
      </header> */}

      {/* Main Content */}
      <main className="flex-grow py-4 md:mx-0 font-poppins">
        {/* Attendance History Section */}
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-6 mb-6 mx-2">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold dark:text-white">Attendance History</h2>
          </div>
          
          {/* chart */}
          <div className="h-64">
          <BarChart/>
          </div>
        </div>

        {/* Employee Attendance Section */}
        <h2 className="text-lg font-semibold mb-4 dark:text-white px-4">All Employees</h2>
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-6 mx-2">
          {/* Search and Date Filter */}
          <div className="flex flex-col md:flex-row items-center justify-between  gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search"
                className="border border-gray-300 mb-3 dark:bg-black dark:border-gray-700 dark:text-white dark:focus:outline-none rounded pl-10 pr-4 py-2 w-full md:w-64"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
            </div>
            
            <div className="flex gap-12 mb-4">
              <div className="relative">
                {/* <label className="block text-sm text-gray-500 mb-1 dark:text-white">Start Date:</label> */}
                <div className="flex items-center border-none">
                  <FlatpickrDateInput value={startDate} onChange={setStartDate} label="Start Date" />
                </div>
              </div>
              
              <div className="relative">
                {/* <label className="block text-sm text-gray-500 mb-1 dark:text-white">End Date:</label> */}
                <div className="flex items-center border-none">
                  <FlatpickrDateInput value={endDate} onChange={setEndDate} label="End Date" />
                </div>
              </div>
            </div>
          </div>
          
          {/* Attendance Table */}
          <div className="overflow-x-auto bg-white dark:bg-gray-900 shadow-md rounded-lg">
            <table className="min-w-full table-auto md:table-fixed text-sm">
              <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
                <tr >
                  <th className="py-2 px-4 dark:text-white text-left text-xs font-semibold md:text-sm text-black whitespace-nowrap">Emp ID</th>
                  <th className="py-2 px-4 dark:text-white text-left text-xs font-semibold md:text-sm text-black whitespace-nowrap">Full Name</th>
                  <th className="py-2 px-4 dark:text-white text-left text-xs font-semibold md:text-sm text-black whitespace-nowrap">Date</th>
                  <th className="py-2 px-4 dark:text-white text-left text-xs font-semibold md:text-sm text-black whitespace-nowrap">Clock In</th>
                  <th className="py-2 px-4 dark:text-white text-left text-xs font-semibold md:text-sm text-black whitespace-nowrap">Clock Out</th>
                  <th className="py-2 px-4 dark:text-white text-left text-xs font-semibold md:text-sm text-black whitespace-nowrap">Work Hours</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((employee, index) => (
                  <tr key={index} className="border-b dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800">
                    <td className="py-2 dark:text-white px-4 text-xs text-left md:text-sm whitespace-nowrap">{employee.empId}</td>
                    <td className="py-2 dark:text-white px-4 text-xs text-left md:text-sm whitespace-nowrap">{employee.fullName}</td>
                    <td className="py-2 dark:text-white px-4 text-xs text-left md:text-sm whitespace-nowrap">{employee.date}</td>
                    <td className="py-2 dark:text-white px-4 text-xs text-left md:text-sm whitespace-nowrap">{employee.clockIn}</td>
                    <td className="py-2 dark:text-white px-4 text-xs text-left md:text-sm whitespace-nowrap">{employee.clockOut}</td>
                    <td className="py-2 dark:text-white px-4 text-xs text-left md:text-sm whitespace-nowrap">{employee.workHours}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <div className="flex justify-end items-center gap-1 mt-4">
            <button className="flex items-center justify-center w-8 h-8 rounded bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700">
              <ChevronLeft size={16} />
            </button>
            <button className="flex items-center justify-center w-8 h-8 rounded bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700">1</button>
            <button className="flex items-center justify-center w-8 h-8 rounded bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}